/**
 * Softphone Coordinator Store
 *
 * Holds which softphone version is active (legacy vs new) and diagnostics panel
 * access. Persisted by personId in localStorage.
 *
 * The active version is no longer a user preference: it mirrors the
 * `communicator-softphone-v2` feature flag, which is the single controller.
 * The value is persisted only so non-React code (PhoneStore, PusherStore, ...)
 * can read it synchronously via `SoftphoneCoordinator.readPreferenceSync`
 * before LaunchDarkly has resolved.
 */

import { DEFAULT_IS_NEW_SOFTPHONE_ENABLED } from 'Modules/softphone/constants/softphoneVersion';
import { sendIpcLog } from 'Utils/ipcRendererEvents';
import { create, StateCreator } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createPartitionedStorage, isValidPersonId } from '../storage';
import { SoftphoneCoordinatorStore, StoreType } from './index.types';

const storeCache = new Map<string, StoreType>();

const createCoordinatorSlice: StateCreator<SoftphoneCoordinatorStore> = (
  set
) => ({
  isNewSoftphoneEnabled: DEFAULT_IS_NEW_SOFTPHONE_ENABLED,
  enableIncomingCalls: true,
  isDiagnosticsPanelEnabled: false,

  setIsNewSoftphoneEnabled: (enabled: boolean) => {
    set({ isNewSoftphoneEnabled: enabled });
  },

  /**
   * Align the persisted version with the `communicator-softphone-v2` flag.
   *
   * A reload is required whenever the value changes because the softphone that
   * matched the previous value has already claimed the SIP registration for
   * this session (PhoneStore registers legacy SIP once, on login). Reloading
   * lets the next session start from the persisted value with a single
   * registration.
   *
   * No-op when the value already matches, so this never loops on reload.
   */
  syncWithFeatureFlag: (enabled: boolean) => {
    set((state) => {
      if (state.isNewSoftphoneEnabled === enabled) {
        return state;
      }

      setTimeout(() => {
        sendIpcLog('info', 'Window was reloaded', {
          reason: `softphone v2 feature flag ${enabled ? 'enabled' : 'disabled'}`,
        });
        window.location.reload();
      }, 100);

      return { isNewSoftphoneEnabled: enabled };
    });
  },

  toggleIncomingCalls: (enabled?: boolean) => {
    set((state) => ({
      enableIncomingCalls: enabled ?? !state.enableIncomingCalls,
    }));
  },

  setEnableIncomingCalls: (enabled: boolean) => {
    set({ enableIncomingCalls: enabled });
  },

  toggleDiagnosticsPanel: (enabled?: boolean) => {
    set((state) => ({
      isDiagnosticsPanelEnabled: enabled ?? !state.isDiagnosticsPanelEnabled,
    }));
  },

  setIsDiagnosticsPanelEnabled: (enabled: boolean) => {
    set({ isDiagnosticsPanelEnabled: enabled });
  },
});

export const createSoftphoneCoordinatorStore = (
  personId?: number | string | null
) => {
  const cacheKey = String(personId);

  const cachedStore = storeCache.get(cacheKey);
  if (cachedStore) {
    return cachedStore;
  }

  // Non-persisted store for invalid personId - MUST be cached to prevent
  // creating new store instances on each render which causes instability
  if (!isValidPersonId(personId, storeCache)) {
    const nonPersistedStore = create<SoftphoneCoordinatorStore>()(
      createCoordinatorSlice
    );

    // Cache the non-persisted store to ensure stability
    storeCache.set(cacheKey, nonPersistedStore);
    return nonPersistedStore;
  }

  // Persisted store for valid personId
  const store = create<SoftphoneCoordinatorStore>()(
    persist(createCoordinatorSlice, {
      name: 'softphone-coordinator',
      storage: createJSONStorage(() => createPartitionedStorage(personId)),
    })
  );

  storeCache.set(cacheKey, store);
  return store;
};

export const useSoftphoneCoordinatorStore = (
  personId?: number | string | null
) => {
  // Normalize personId: only use valid IDs (> 0), otherwise use null
  // This prevents creating different stores for invalid IDs like -1, 0, undefined
  const numericPersonId = typeof personId === 'string' ? parseInt(personId, 10) : personId;
  const normalizedPersonId = numericPersonId && numericPersonId > 0 ? numericPersonId : null;
  return createSoftphoneCoordinatorStore(normalizedPersonId);
};
